import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line, Scatter } from "react-chartjs-2";
import { Alert, Button, DatePicker, Popover, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { BACKEND_DATE_FORMAT, COLORS, UI_DATE_FORMAT } from "../../../consts";
import dayjs from "dayjs";
import { getFilePreSignedUrl } from "../../../utils/api";
import { gatherExcludedDatesForRow } from "./utils";
import { useUser } from "../../../context/UserContext";
import {
  endpointGrowthIsNegative,
  isPositiveTrendCorrectionEnabled,
} from "../../../utils/positiveTrendCorrection";
import { blobDate, blobSitePrefix } from "../../../utils/demoSiteBlob";

const graphColors = COLORS.graphColors;
const { RangePicker } = DatePicker;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hexToRgb = (hexColor) => {
  const hex = hexColor?.replace("#", "");
  if (!hex || (hex.length !== 3 && hex.length !== 6)) return null;

  const normalized =
    hex.length === 3
      ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
      : hex;

  const parsed = Number.parseInt(normalized, 16);
  if (Number.isNaN(parsed)) return null;

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
};

const toHeatColor = (baseColor, value, maxValue) => {
  const { r, g, b } = hexToRgb(baseColor) || {
    r: 24,
    g: 144,
    b: 255,
  };
  const safeMax = maxValue > 0 ? maxValue : 1;
  const ratio = clamp(value / safeMax, 0, 1);
  const alpha = 0.08 + ratio * 0.92;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const parseBinRange = (label) => {
  const rawLabel = String(label).trim();
  if (rawLabel.includes("-")) {
    const [startRaw, endRaw] = rawLabel.split("-");
    const start = Number(startRaw);
    const end = Number(endRaw);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      const lower = Math.min(start, end);
      const upper = Math.max(start, end);
      return { start: lower, end: upper > lower ? upper : lower + 1 };
    }
  }

  const start = Number(rawLabel);
  if (!Number.isFinite(start)) return null;
  return { start, end: start + 1 };
};

const getQuantileFromDistribution = (binRanges, percentages, quantile) => {
  const safePercentages = percentages.map((value) =>
    Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0,
  );
  const total = safePercentages.reduce((sum, value) => sum + value, 0);
  if (total <= 0 || binRanges.length === 0) return null;

  const target = total * clamp(quantile, 0, 1);
  let cumulative = 0;

  for (let i = 0; i < binRanges.length; i += 1) {
    const binRange = binRanges[i];
    if (!binRange) continue;
    const current = safePercentages[i];
    const next = cumulative + current;
    if (next >= target) {
      if (current <= 0) return binRange.start;
      const ratio = clamp((target - cumulative) / current, 0, 1);
      return binRange.start + (binRange.end - binRange.start) * ratio;
    }
    cumulative = next;
  }

  for (let i = binRanges.length - 1; i >= 0; i -= 1) {
    if (binRanges[i]) return binRanges[i].end;
  }
  return null;
};

const getModeFromDistribution = (binRanges, percentages) => {
  if (!binRanges.length || !percentages.length) return null;
  let maxIndex = 0;
  for (let i = 1; i < percentages.length; i += 1) {
    if ((percentages[i] ?? 0) > (percentages[maxIndex] ?? 0)) {
      maxIndex = i;
    }
  }
  const binRange = binRanges[maxIndex];
  if (!binRange) return null;
  return (binRange.start + binRange.end) / 2;
};

const getQuantileFromValues = (values, quantile) => {
  const numericValues = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  if (!numericValues.length) return null;

  const q = clamp(quantile, 0, 1);
  const position = (numericValues.length - 1) * q;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);

  if (lowerIndex === upperIndex) return numericValues[lowerIndex];

  const lowerValue = numericValues[lowerIndex];
  const upperValue = numericValues[upperIndex];
  const ratio = position - lowerIndex;
  return lowerValue + (upperValue - lowerValue) * ratio;
};

const getSpecSplitPercentages = (dataset, specBounds, binRanges) => {
  const rawSizes = (dataset.rawSizes || [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (rawSizes.length > 0) {
    const under = rawSizes.filter((value) => value < specBounds.lower).length;
    const inside = rawSizes.filter(
      (value) => value >= specBounds.lower && value <= specBounds.upper,
    ).length;
    const over = rawSizes.filter((value) => value > specBounds.upper).length;
    const total = rawSizes.length;
    return {
      under: (under / total) * 100,
      inside: (inside / total) * 100,
      over: (over / total) * 100,
    };
  }

  const percentages = dataset.data?.slice(0, binRanges.length) || [];
  let under = 0;
  let inside = 0;
  let over = 0;

  percentages.forEach((value, index) => {
    const numericValue = Number.isFinite(Number(value)) ? Number(value) : 0;
    const binRange = binRanges[index];
    if (!binRange) return;
    const center = (binRange.start + binRange.end) / 2;
    if (center < specBounds.lower) under += numericValue;
    else if (center > specBounds.upper) over += numericValue;
    else inside += numericValue;
  });

  const total = under + inside + over;
  if (total <= 0) return { under: 0, inside: 0, over: 0 };

  return {
    under: (under / total) * 100,
    inside: (inside / total) * 100,
    over: (over / total) * 100,
  };
};
const DistributionGraph = ({
  currentOrg,
  currentSite,
  currentCrop,
  currentGreenhouse,
  currentPlot,
  BIN_END,
  graphData,
  allRowGraphData,
  dateList,
  minSize,
  maxSize,
  visibility,
  setVisibility,
  showPercentage,
  setShowPercentage,
  filteredGraphData,
  setFilteredGraphData,
  averageData,
  tableData,
  filterRules,
  setAverageData,
  setAllRowAverageData,
  setNdviAverageData,
  setAllRowNdviAverageData,
  selectedDates,
  setSelectedDates,
  handleDateChange,
  setExcludedLeafSizeData,
  siteMetadata,
  isFullscreen = false,
}) => {
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
  const KEY_IN_DATA = "leaf_lengths_cm";
  const [graphType, setGraphType] = useState("line");
  const { userData, isAdmin } = useUser();
  // OF-129: per-metric correction of the 80-95 line (the graphData reorder
  // upstream only guarantees the Average line is monotonic). Display-only.
  const applyTrendCorrection = useMemo(
    () =>
      isPositiveTrendCorrectionEnabled({
        isAdmin,
        site: currentSite,
        cropName: currentCrop?.name,
      }),
    [isAdmin, currentSite, currentCrop],
  );
  const isSuperUser = Boolean(userData?.is_superuser);

  const tableDataRef = useRef(tableData);
  const filterRulesRef = useRef(filterRules);

  const [options, setOptions] = useState({
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw.toFixed(1)}%`;
          },
        },
      },
      greenBandPlugin: {
        show: false,
        minSize: minSize,
        maxSize: maxSize,
        color: "rgba(0, 255, 0, 0.2)",
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Spinach Leaf Size (cm)",
          font: {
            size: 16,
          },
        },
        grid: { display: false },
        ticks: {
          autoSkip: false,
          font: {
            size: 14,
          },
        },
      },
      y: {
        title: {
          display: true,
          text: "Percentage of Spinach leaves",
          font: {
            size: 16,
          },
        },
        grid: { display: false },
        beginAtZero: false,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
    },
  });

  const distributionLabels = Array.from(
    { length: BIN_END - 1 },
    (_, i) => `${i}-${i + 1}`,
  );

  const chartDatasets = filteredGraphData.map((dataset) => {
    return {
      ...dataset,
      type: "line",
      tension: 0.3,
      borderWidth: 2,
      pointRadius: 2,
      pointHoverRadius: 4,
      hidden: !visibility[dataset.label],
    };
  });

  const chartData = { labels: distributionLabels, datasets: chartDatasets };

  const chartOptions = options;

  const graphTypeOptions = [
    { label: "Line", value: "line" },
    { label: "Heatmap", value: "heatmap" },
    { label: "Quantile Trend", value: "quantile" },
    { label: "Spec Mix (100%)", value: "specMix" },
    { label: "ECDF Trend", value: "ecdf" },
    { label: "Delta Trend", value: "delta" },
    { label: "In-Spec KPI Trend", value: "kpi" },
  ];

  const graphInfoByType = {
    line: {
      short:
        "Line Distribution: Compare how the full size distribution shape shifts by date.",
      details: [
        "Each line is one date.",
        "Y values are per-bin percentages: (bin count / total leaves on that date) * 100.",
      ],
      relevance:
        "Best for seeing peak movement, spread, and tail behavior across forecast dates.",
    },
    heatmap: {
      short:
        "Heatmap: Quick density scan across dates and size bins (higher opacity = higher percentage).",
      details: [
        "Rows represent dates; columns represent size bins.",
        "Cell intensity is normalized by that view's max bin percentage.",
      ],
      relevance:
        "Best for quickly spotting where concentration bands move over time.",
    },
    quantile: {
      short:
        "Quantile Trend: Tracks P25, P50 (median), P75, and mode over dates.",
      details: [
        "P25/P50/P75 are computed from raw sizes (sorted + interpolated quantiles).",
        "Mode is estimated from the highest histogram bin midpoint.",
      ],
      relevance:
        "Best for median drift and spread changes without reading every distribution curve.",
    },
    specMix: {
      short:
        "Spec Mix (100%): Shows under-spec, in-spec, and over-spec share by date.",
      details: [
        "Primary calculation uses raw sizes against current spec bounds.",
        "Fallback uses histogram-bin center approximation when raw sizes are unavailable.",
      ],
      relevance: "Best for operational quality split tracking over time.",
    },
    ecdf: {
      short: "ECDF Trend: Cumulative distribution by size for each date.",
      details: [
        "For each date: cumulative sum of bin percentages, normalized to 0-100%.",
        "At any x, the curve shows % of leaves <= that size.",
      ],
      relevance:
        "Best for percentile reading and direct stochastic dominance comparison between dates.",
    },
    delta: {
      short:
        "Delta Trend: Shows median shift and IQR-width shift relative to first visible date.",
      details: [
        "Median shift = P50(date) - P50(baseline).",
        "IQR width shift = (P75-P25)(date) - (P75-P25)(baseline).",
      ],
      relevance:
        "Best for quantifying movement direction and spread change with a stable baseline.",
    },
    kpi: {
      short: "In-Spec KPI Trend: Single KPI trend for in-spec percentage.",
      details: [
        "In-spec % = count(lower <= size <= upper) / total * 100 from raw sizes.",
        "Uses same spec bounds as the Spec Mix view.",
      ],
      relevance: "Best for quick decision dashboards and target tracking.",
    },
  };
  const activeGraphInfo = graphInfoByType[graphType] || graphInfoByType.line;

  const visibleDatasets = useMemo(
    () => filteredGraphData.filter((dataset) => visibility[dataset.label] !== false),
    [filteredGraphData, visibility],
  );

  const rowColorByLabel = useMemo(
    () =>
      visibleDatasets.reduce((acc, dataset, index) => {
        acc[dataset.label] =
          dataset.borderColor ||
          dataset.backgroundColor ||
          graphColors[index % graphColors.length];
        return acc;
      }, {}),
    [visibleDatasets],
  );

  const heatmapMaxValue = useMemo(() => {
    let maxValue = 0;
    visibleDatasets.forEach((dataset) => {
      dataset.data?.slice(0, distributionLabels.length).forEach((value) => {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
          maxValue = Math.max(maxValue, numericValue);
        }
      });
    });
    return maxValue;
  }, [visibleDatasets, distributionLabels.length]);

  const heatmapData = useMemo(
    () =>
      visibleDatasets.flatMap((dataset) =>
        dataset.data?.slice(0, distributionLabels.length).map((value, binIndex) => ({
          x: distributionLabels[binIndex],
          y: dataset.label,
          v: Number.isFinite(Number(value)) ? Number(value) : 0,
        })),
      ),
    [visibleDatasets, distributionLabels],
  );

  const heatmapOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: () => "Distribution Density",
            label: (tooltipItem) => {
              const { x, y, v } = tooltipItem.raw || {};
              return `${y} | ${x}: ${(v || 0).toFixed(2)}%`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "category",
          labels: distributionLabels,
          title: {
            display: true,
            text: "Spinach Leaf Size (cm)",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 14 },
          },
        },
        y: {
          type: "category",
          labels: visibleDatasets.map((dataset) => dataset.label),
          reverse: true,
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
      },
    }),
    [distributionLabels, isFullscreen, visibleDatasets],
  );

  const heatmapChartData = useMemo(
    () => ({
      datasets: [
        {
          label: "Distribution Density",
          data: heatmapData,
          showLine: false,
          pointStyle: "rectRounded",
          borderWidth: 0,
          pointRadius: (context) => {
            const chartArea = context.chart?.chartArea;
            if (!chartArea) return 6;
            const xCount = Math.max(distributionLabels.length, 1);
            const yCount = Math.max(visibleDatasets.length, 1);
            const cellWidth = chartArea.width / xCount;
            const cellHeight = chartArea.height / yCount;
            return Math.max(2, Math.min(cellWidth, cellHeight) / 2 - 1);
          },
          pointHoverRadius: 8,
          backgroundColor: (context) => {
            const value = Number(context.raw?.v) || 0;
            const rowLabel = context.raw?.y;
            const rowColor = rowColorByLabel[rowLabel] || COLORS.primaryBlue;
            return toHeatColor(rowColor, value, heatmapMaxValue);
          },
        },
      ],
    }),
    [
      distributionLabels.length,
      heatmapData,
      heatmapMaxValue,
      rowColorByLabel,
      visibleDatasets.length,
    ],
  );

  const binRanges = useMemo(
    () => distributionLabels.map((label) => parseBinRange(label)),
    [distributionLabels],
  );

  const quantileChartData = useMemo(() => {
    const labels = visibleDatasets.map((dataset) => dataset.label);
    const p25 = [];
    const p50 = [];
    const p75 = [];
    const mode = [];

    visibleDatasets.forEach((dataset) => {
      const percentages = dataset.data?.slice(0, binRanges.length) || [];
      const rawSizes = dataset.rawSizes || [];
      const q25 = getQuantileFromValues(rawSizes, 0.25);
      const q50 = getQuantileFromValues(rawSizes, 0.5);
      const q75 = getQuantileFromValues(rawSizes, 0.75);

      p25.push(
        q25 !== null
          ? q25
          : getQuantileFromDistribution(binRanges, percentages, 0.25),
      );
      p50.push(
        q50 !== null
          ? q50
          : getQuantileFromDistribution(binRanges, percentages, 0.5),
      );
      p75.push(
        q75 !== null
          ? q75
          : getQuantileFromDistribution(binRanges, percentages, 0.75),
      );
      mode.push(getModeFromDistribution(binRanges, percentages));
    });

    return {
      labels,
      datasets: [
        {
          label: "P25",
          data: p25,
          borderColor: "rgba(59, 130, 246, 0.4)",
          backgroundColor: "rgba(59, 130, 246, 0.10)",
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
        {
          label: "P75",
          data: p75,
          borderColor: "rgba(59, 130, 246, 0.4)",
          backgroundColor: "rgba(59, 130, 246, 0.10)",
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: "-1",
        },
        {
          label: "P50 (Median)",
          data: p50,
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          borderWidth: 2.5,
          pointRadius: 3,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
        {
          label: "Mode",
          data: mode,
          borderColor: "#22c55e",
          backgroundColor: "#22c55e",
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 2,
          spanGaps: true,
          tension: 0.3,
          fill: false,
        },
      ],
    };
  }, [binRanges, visibleDatasets]);

  const quantileOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)} cm`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
        y: {
          title: {
            display: true,
            text: "Spinach Leaf Size (cm)",
            font: { size: 16 },
          },
          grid: { display: false },
          beginAtZero: true,
          ticks: {
            font: { size: 14 },
          },
        },
      },
    }),
    [isFullscreen],
  );

  const specBounds = useMemo(
    () => ({
      lower: Math.min(Number(minSize) || 0, Number(maxSize) || 0),
      upper: Math.max(Number(minSize) || 0, Number(maxSize) || 0),
    }),
    [minSize, maxSize],
  );

  const ecdfChartData = useMemo(
    () => ({
      labels: distributionLabels,
      datasets: filteredGraphData.map((dataset, index) => {
        const percentages = dataset.data?.slice(0, binRanges.length) || [];
        const safePercentages = percentages.map((value) =>
          Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0,
        );
        const total = safePercentages.reduce((sum, value) => sum + value, 0);
        let cumulative = 0;
        const cdf = safePercentages.map((value) => {
          cumulative += value;
          return total > 0 ? (cumulative / total) * 100 : 0;
        });

        return {
          label: dataset.label,
          data: cdf,
          borderColor:
            dataset.borderColor ||
            dataset.backgroundColor ||
            graphColors[index % graphColors.length],
          backgroundColor:
            dataset.backgroundColor ||
            dataset.borderColor ||
            graphColors[index % graphColors.length],
          tension: 0.25,
          borderWidth: 2,
          pointRadius: 1.5,
          pointHoverRadius: 4,
          hidden: !visibility[dataset.label],
          fill: false,
          spanGaps: true,
        };
      }),
    }),
    [binRanges.length, distributionLabels, filteredGraphData, visibility],
  );

  const ecdfOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Spinach Leaf Size (cm)",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 14 } },
        },
        y: {
          title: {
            display: true,
            text: "Cumulative Share (%)",
            font: { size: 16 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [isFullscreen],
  );

  const specMixChartData = useMemo(() => {
    const labels = visibleDatasets.map((dataset) => dataset.label);
    const underSpec = [];
    const inSpec = [];
    const overSpec = [];

    visibleDatasets.forEach((dataset) => {
      const split = getSpecSplitPercentages(dataset, specBounds, binRanges);
      underSpec.push(split.under);
      inSpec.push(split.inside);
      overSpec.push(split.over);
    });

    return {
      labels,
      datasets: [
        {
          label: "Under Spec",
          data: underSpec,
          borderColor: "#f97316",
          backgroundColor: "rgba(249, 115, 22, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
        {
          label: "In Spec",
          data: inSpec,
          borderColor: "#22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
        {
          label: "Over Spec",
          data: overSpec,
          borderColor: "#6366f1",
          backgroundColor: "rgba(99, 102, 241, 0.25)",
          pointRadius: 0,
          tension: 0.3,
          fill: true,
          stack: "spec",
        },
      ],
    };
  }, [binRanges, specBounds, visibleDatasets]);

  const specMixOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 12 },
          },
        },
        y: {
          title: {
            display: true,
            text: "Share (%)",
            font: { size: 16 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          stacked: true,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [isFullscreen],
  );

  const deltaChartData = useMemo(() => {
    const labels = visibleDatasets.map((dataset) => dataset.label);
    const metrics = visibleDatasets.map((dataset) => {
      const percentages = dataset.data?.slice(0, binRanges.length) || [];
      const rawSizes = dataset.rawSizes || [];
      const q25 =
        getQuantileFromValues(rawSizes, 0.25) ??
        getQuantileFromDistribution(binRanges, percentages, 0.25);
      const q50 =
        getQuantileFromValues(rawSizes, 0.5) ??
        getQuantileFromDistribution(binRanges, percentages, 0.5);
      const q75 =
        getQuantileFromValues(rawSizes, 0.75) ??
        getQuantileFromDistribution(binRanges, percentages, 0.75);
      return {
        median: Number.isFinite(q50) ? q50 : null,
        iqr:
          Number.isFinite(q25) && Number.isFinite(q75) ? q75 - q25 : null,
      };
    });

    const baseline = metrics.find(
      (item) => Number.isFinite(item.median) && Number.isFinite(item.iqr),
    );
    const baselineMedian = baseline?.median ?? 0;
    const baselineIqr = baseline?.iqr ?? 0;

    return {
      labels,
      datasets: [
        {
          label: "Median Shift (cm)",
          data: metrics.map((item) =>
            Number.isFinite(item.median) ? item.median - baselineMedian : null,
          ),
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          borderWidth: 2.5,
          pointRadius: 2.5,
          tension: 0.3,
          spanGaps: true,
          fill: false,
        },
        {
          label: "IQR Width Shift (cm)",
          data: metrics.map((item) =>
            Number.isFinite(item.iqr) ? item.iqr - baselineIqr : null,
          ),
          borderColor: "#22c55e",
          backgroundColor: "#22c55e",
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 2,
          tension: 0.3,
          spanGaps: true,
          fill: false,
        },
        {
          label: "Baseline",
          data: labels.map(() => 0),
          borderColor: "rgba(107, 114, 128, 0.55)",
          backgroundColor: "rgba(107, 114, 128, 0.55)",
          borderDash: [3, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0,
          fill: false,
        },
      ],
    };
  }, [binRanges, visibleDatasets]);

  const deltaOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)} cm`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 12 } },
        },
        y: {
          title: {
            display: true,
            text: "Change From Baseline (cm)",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: { font: { size: 14 } },
        },
      },
    }),
    [isFullscreen],
  );

  const kpiChartData = useMemo(() => {
    const labels = visibleDatasets.map((dataset) => dataset.label);
    const inSpecSeries = visibleDatasets.map(
      (dataset) => getSpecSplitPercentages(dataset, specBounds, binRanges).inside,
    );

    return {
      labels,
      datasets: [
        {
          label: "In Spec (%)",
          data: inSpecSeries,
          borderColor: "#22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.2)",
          borderWidth: 2.5,
          pointRadius: 2.5,
          tension: 0.3,
          fill: true,
          spanGaps: true,
        },
      ],
    };
  }, [binRanges, specBounds, visibleDatasets]);

  const kpiOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.dataset.label}: ${(tooltipItem.raw ?? 0).toFixed(2)}%`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Forecast Date",
            font: { size: 16 },
          },
          grid: { display: false },
          ticks: { autoSkip: false, font: { size: 12 } },
        },
        y: {
          title: {
            display: true,
            text: "In Spec Share (%)",
            font: { size: 16 },
          },
          grid: { display: false },
          min: 0,
          max: 100,
          ticks: {
            callback: (value) => `${value}%`,
            font: { size: 14 },
          },
        },
      },
    }),
    [isFullscreen],
  );

  const greenBandPlugin = {
    id: "greenBandPlugin",
    beforeDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;
      const xScale = chart.scales.x;

      if (options.show) {
        const minPixel = xScale.getPixelForValue(options.minSize);
        const maxPixel = xScale.getPixelForValue(options.maxSize);
        ctx.fillStyle = options.color;
        ctx.fillRect(
          minPixel,
          chartArea.top,
          maxPixel - minPixel,
          chartArea.bottom - chartArea.top,
        );
      }
    },
  };

  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  useEffect(() => {
    filterRulesRef.current = filterRules;
  }, [filterRules]);

  useEffect(() => {
    applyFilterOnData();
  }, [tableData, filterRules]);

  useEffect(() => {
    setOptions((prevOptions) => ({
      ...prevOptions,
      plugins: {
        ...prevOptions.plugins,
        greenBandPlugin: {
          ...prevOptions.plugins.greenBandPlugin,
          show: showPercentage,
          minSize: minSize,
          maxSize: maxSize,
        },
      },
    }));
  }, [showPercentage, minSize, maxSize]);

  useEffect(() => {
    if (selectedDates && selectedDates.length > 0) {
      updateGraphData(selectedDates[0], selectedDates[1]);
    }
    // OF-129: `tableData` carries the harvest date used to decide whether the
    // distribution should be corrected. Re-run when it arrives so the harvest
    // clip isn't evaluated against an empty tableDataRef (which would fall back
    // to the full range and wrongly reorder post-harvest data).
    // `siteMetadata` supplies the blob prefix used by the percentile fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates, tableData, siteMetadata]);

  const toggleLineVisibility = (label) => {
    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [label]: !prevVisibility[label],
    }));
  };

  const getPercentile80_95Json = async (
    date,
    lengths,
    plotName = currentPlot.name,
  ) => {
    const computeWithAverageData = () =>
      getAverageFromPercentile(lengths, 80, 95);

    try {
      // Demo/clone sites read another site's blobs (CLOUD_SITE_PREFIX) at that
      // site's dates (CLOUD_DATE_OFFSETS per bay); plain sites are unaffected.
      const jsonURL = `${blobSitePrefix(siteMetadata, currentSite)}/${currentCrop.name}/${blobDate(siteMetadata, currentGreenhouse.name, date)}/${currentGreenhouse.name}/${plotName}/percentile_(80, 95)_data.json`;
      const preSignedUrl = await getFilePreSignedUrl(
        currentOrg,
        currentSite.id,
        currentCrop.name,
        jsonURL,
        siteMetadata?.CLOUD_STORAGE_NAME || "azure",
        true,
        true,
      );

      const jsonUrl = preSignedUrl?.data?.data?.url;
      if (!jsonUrl) return computeWithAverageData();

      const response = await fetch(jsonUrl);

      if (!response.ok) return computeWithAverageData();

      const jsonData = await response.json();

      return jsonData?.average_length_cm || computeWithAverageData();
    } catch {
      return computeWithAverageData();
    }
  };

  const _percentiles = (arr, qs) => {
    if (arr.length === 0) return {};
    // Make a copy of the array and sort it
    const sorted = arr.slice().sort(function (a, b) {
      return a - b;
    });
    const n = sorted.length;

    let result = {};

    for (let i = 0; i < qs.length; i++) {
      const q = qs[i];
      const pos = (q / 100) * (n - 1);
      const base = Math.floor(pos);
      const rest = pos - base;
      if (sorted[base + 1] !== undefined) {
        result[q] = sorted[base] + rest * (sorted[base + 1] - sorted[base]);
      } else {
        result[q] = sorted[base];
      }
    }
    return result;
  };

  const getAverageFromPercentile = (
    lengthList,
    minPercentile,
    maxPercentile,
  ) => {
    if (!Array.isArray(lengthList) || lengthList.length === 0) {
      return null;
    }

    // Sort the leaf length list in ascending order
    const sortedList = [...lengthList].sort((a, b) => a - b);

    // Calculate the indices for the given percentiles
    const minIndex = Math.floor((minPercentile / 100) * sortedList.length);
    const maxIndex = Math.ceil((maxPercentile / 100) * sortedList.length);

    // Extract the slice of values within the percentile range
    const percentileSlice = sortedList.slice(minIndex, maxIndex);
    if (!percentileSlice.length) {
      return null;
    }

    // Calculate the average of the slice
    const sum = percentileSlice.reduce((acc, value) => acc + value, 0);
    const average = sum / percentileSlice.length;
    return Number.isFinite(average) ? parseFloat(average.toFixed(1)) : null;
  };

  const applyFilterOnData = (
    data = averageData,
    filGraphData = filteredGraphData,
  ) => {
    const latestTableData = tableDataRef.current;
    const latestFilterRules = filterRulesRef.current;
    if (data?.length || !latestFilterRules?.length || !latestTableData?.length) {
      setAverageData(data);
      setFilteredGraphData(filGraphData);
    }
    if (!data?.length || !latestFilterRules?.length || !latestTableData?.length)
      return;
    const plantingDate = latestTableData?.[0]?.plantingDate || null;
    const harvestDate = latestTableData?.[0]?.harvestDate || null;
    const excluded = gatherExcludedDatesForRow(
      latestFilterRules,
      data,
      plantingDate,
      harvestDate,
    );
    const excludedSet = new Set(excluded.map((e) => e.date));
    const filteredAverageData = data.filter(
      (item) => !excludedSet.has(item.date),
    );
    setAverageData(filteredAverageData);
    setExcludedLeafSizeData(excluded);
    const updatedGraphData = filGraphData.filter(
      (item) => !excludedSet.has(item.label),
    );
    setFilteredGraphData(updatedGraphData);
  };

  const updateAverageData = async (startDate, endDate, filteredGraphData) => {
    const start = startDate.format(BACKEND_DATE_FORMAT);
    const end = endDate.format(BACKEND_DATE_FORMAT);

    const filteredEntries = Object.entries(graphData).filter(
      ([date]) => date >= start && date <= end,
    );

    const filteredData = await Promise.all(
      filteredEntries.map(async ([date, values]) => {
        const lengths = (values[KEY_IN_DATA] || []).map((item) => item[1]);
        // const percents = percentiles(lengths, [25, 90, 95]);
        let averagePercentile80_95 =
          values?.percentiles?.["80_95"]?.average_length_cm;
        if (!averagePercentile80_95) {
          averagePercentile80_95 = await getPercentile80_95Json(date, lengths);
        }

        return {
          date: dayjs(date).format(GRAPH_UI_DATE_FORMAT),
          averageLength: values.average_length_cm,
          stdDev: values.std_dev_cm,
          // percentile_25: percents[25],
          // percentile_90: percents[90],
          // percentile_95: percents[95],
          averagePercentile80_95,
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          tension: 0.3,
        };
      }),
    );

    const sortedData = filteredData.sort((a, b) =>
      dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
        dayjs(b.date, GRAPH_UI_DATE_FORMAT),
      ),
    );
    applyFilterOnData(sortedData, filteredGraphData);
  };

  const updateNdviAverageData = (startDate, endDate) => {
    const start = startDate.format(BACKEND_DATE_FORMAT);
    const end = endDate.format(BACKEND_DATE_FORMAT);

    const filteredData = Object.entries(graphData)
      .filter(([date]) => date >= start && date <= end)
      .map(([date, values]) => {
        return {
          date: dayjs(date).format(GRAPH_UI_DATE_FORMAT),
          ndviMean: values.ndvi_mean,
          ndviMedian: values.ndvi_median,
          ndviStdDev: values.ndvi_std,
          ndviVar: values.ndvi_var,
          ndviMinValue: values.ndvi_minval,
          ndviMaxValue: values.ndvi_maxval,
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          tension: 0.3,
        };
      })
      .sort((a, b) =>
        dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
          dayjs(b.date, GRAPH_UI_DATE_FORMAT),
        ),
      );

    setNdviAverageData(filteredData);
  };

  function calculateMean(numbers) {
    const total = numbers.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0,
    );
    return parseFloat((total / numbers.length).toFixed(1));
  }

  function calculateStandardDeviation(numbers) {
    const mean = calculateMean(numbers);
    const variance =
      numbers.reduce((accumulator, currentValue) => {
        return accumulator + Math.pow(currentValue - mean, 2);
      }, 0) / numbers.length;
    return parseFloat(Math.sqrt(variance).toFixed(1));
  }

  const updateAllRowAverageData = async (startDate, endDate) => {
    if (!allRowGraphData || Object.keys(allRowGraphData).length === 0) return;
    const start = startDate.format(BACKEND_DATE_FORMAT);
    const end = endDate.format(BACKEND_DATE_FORMAT);

    const filteredData = Object.entries(allRowGraphData)
      .filter(([date]) => date >= start && date <= end)
      .map(([date, values]) => {
        const lengths = values[KEY_IN_DATA].map((item) => item[1]);
        // const percents = percentiles(lengths, [25, 90, 95]);

        return {
          date: dayjs(date).format(GRAPH_UI_DATE_FORMAT),
          averageLength: calculateMean(lengths),
          stdDev: calculateStandardDeviation(lengths),
          lengths: lengths,
          // percentile_25: percents[25],
          // percentile_90: percents[90],
          // percentile_95: percents[95],
          averagePercentile80_95: getAverageFromPercentile(lengths, 80, 95),
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          tension: 0.3,
        };
      })
      .sort((a, b) =>
        dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
          dayjs(b.date, GRAPH_UI_DATE_FORMAT),
        ),
      );

    setAllRowAverageData(filteredData);
  };

  const updateAllRowNdviAverageData = (startDate, endDate) => {
    if (!allRowGraphData || Object.keys(allRowGraphData).length === 0) return;
    const start = startDate.format(BACKEND_DATE_FORMAT);
    const end = endDate.format(BACKEND_DATE_FORMAT);

    const filteredData = Object.entries(allRowGraphData)
      .filter(([date]) => date >= start && date <= end)
      .map(([date, values]) => {
        return {
          date: dayjs(date).format(GRAPH_UI_DATE_FORMAT),
          ndviMean: values.ndvi_mean,
          ndviMedian: values.ndvi_median,
          ndviStdDev: values.ndvi_std,
          ndviVar: values.ndvi_var,
          ndviMinValue: values.ndvi_minval,
          ndviMaxValue: values.ndvi_maxval,
          borderColor: COLORS.primaryBlue,
          backgroundColor: COLORS.primaryBlue,
          tension: 0.3,
        };
      })
      .sort((a, b) =>
        dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
          dayjs(b.date, GRAPH_UI_DATE_FORMAT),
        ),
      );

    setAllRowNdviAverageData(filteredData);
  };

  const updateGraphData = (startDate, endDate) => {
    const start = startDate.format(BACKEND_DATE_FORMAT);
    const end = endDate.format(BACKEND_DATE_FORMAT);

    const filteredData = Object.entries(graphData)
      .filter(([date]) => date >= start && date <= end)
      .sort((a, b) =>
        dayjs(a[0], BACKEND_DATE_FORMAT).diff(dayjs(b[0], BACKEND_DATE_FORMAT)),
      ) // Sort entries by date
      .map(([date, values], index) => {
        const rawSizes = (values?.[KEY_IN_DATA] || [])
          .map((item) => Number(item?.[1]))
          .filter((value) => Number.isFinite(value));

        const binEdges = Array.from({ length: BIN_END }, (_, i) => i);
        const hist = binEdges.map((edge, i) => {
          if (i < binEdges.length - 1 && values && KEY_IN_DATA in values) {
            return values[KEY_IN_DATA].map((item) => item[1]).filter(
              (length) => length >= edge && length < binEdges[i + 1],
            ).length;
          }
          return 0;
        });
        const totalProcessedHeadCount = hist.reduce(
          (partialSum, a) => partialSum + a,
          0,
        );
        const histogramsPercentage =
          totalProcessedHeadCount > 0
            ? hist.map((value) => (value / totalProcessedHeadCount) * 100)
            : hist.map(() => 0);

        return {
          label: dayjs(date).format(GRAPH_UI_DATE_FORMAT),
          data: histogramsPercentage,
          fill: false,
          borderColor: graphColors[index % graphColors.length],
          backgroundColor: graphColors[index % graphColors.length],
          tension: 0.3,
          spanGaps: true,
          rawSizes,
          // null/undefined/non-numeric -> NaN (not 0), so a missing average can't
          // masquerade as a real 0 that skews the negative-trend check or sort.
          _avg:
            values?.average_length_cm == null
              ? NaN
              : Number(values.average_length_cm),
        };
      });

    // OF-129: reorder the distribution curves so they "march" by ascending size,
    // so the customer never sees the size distribution shift back to the left.
    // Gated on the CURRENT-CROP (harvest-clipped) average trend — the SAME
    // criterion the leaf-size trend uses — so the two views are corrected (or
    // left alone) together. Date labels + colors stay in their chronological
    // slots; only the per-date histogram payloads are permuted. Display-only.
    let datasets = filteredData;
    if (applyTrendCorrection) {
      const rawHarvestDate = tableDataRef.current?.[0]?.harvestDate;
      const harvestDate = rawHarvestDate ? dayjs(rawHarvestDate) : null;
      // Mirror the harvest-clipped trend line: drop the harvest-day + post-harvest
      // curves so nothing trails behind the ascending march, then reorder ONLY the
      // pre-harvest curves. Admins / non-opted-in sites keep every curve (this
      // whole block is skipped when applyTrendCorrection is false).
      const preHarvest = harvestDate
        ? filteredData.filter((d) =>
            dayjs(d.label, GRAPH_UI_DATE_FORMAT).isBefore(harvestDate, "day"),
          )
        : filteredData;
      datasets = preHarvest;
      const avgs = preHarvest.map((d) => d._avg);
      // require a real average on every point before touching the order
      const allAvgsFinite = avgs.every((v) => Number.isFinite(v));
      if (allAvgsFinite && endpointGrowthIsNegative(avgs)) {
        // sort payloads ascending by average; each curve keeps its date label +
        // color in its chronological slot, only the histogram shape moves.
        const sorted = [...preHarvest].sort((a, b) => a._avg - b._avg);
        datasets = preHarvest.map((orig, i) => ({
          ...orig,
          data: sorted[i].data,
          rawSizes: sorted[i].rawSizes,
          _avg: sorted[i]._avg,
        }));
      }
    }

    // setFilteredGraphData(datasets);

    // Initialize visibility: all lines visible by default
    setVisibility({
      ...datasets.reduce((acc, dataset) => {
        acc[dataset.label] = true; // All distribution lines visible
        return acc;
      }, {}),
      "Average Spinach Leaf Size (cm)": true,
      NDVI: true,
      "Standard Deviation": false,
      "NDVI Range": false,
      "Leaf Size (80-95 percentile) Trend": true,
      "Forecast Leaf Size": true,
    });

    updateAverageData(startDate, endDate, datasets);
    updateAllRowAverageData(startDate, endDate);
    updateNdviAverageData(startDate, endDate);
    updateAllRowNdviAverageData(startDate, endDate);
    setShowPercentage(false);
  };

  useEffect(() => {
    if (dateList && dateList.length > 0) {
      const sortedDates = dateList.sort((a, b) => dayjs(a).diff(dayjs(b)));
      const initialStartDate = dayjs(sortedDates[0]);
      const initialEndDate = dayjs(sortedDates[sortedDates.length - 1]);
      setSelectedDates([initialStartDate, initialEndDate]);
      updateGraphData(initialStartDate, initialEndDate);
    }
  }, [graphData, dateList, siteMetadata]);

  return (
    <div className={`flex w-full flex-col ${isFullscreen ? "h-full min-h-0" : ""}`}>
      <h2 className={`font-bold ${isFullscreen ? "text-xl" : "text-2xl"}`}>
        Distribution Graph
      </h2>
      <div
        className={`graph-header w-full flex flex-wrap justify-between items-center sm:flex-col ${
          isFullscreen ? "mb-2 gap-2" : "mb-4"
        }`}
      >
        <div className="custom-legend flex flex-wrap gap-3 sm:flex-col">
          {filteredGraphData.map((dataset) => (
            <div
              key={dataset.label}
              className="flex items-center mb-1 cursor-pointer"
              onClick={() => toggleLineVisibility(dataset.label)}
            >
              <div
                className="relative w-10 h-0.5 mr-2"
                style={{
                  backgroundColor:
                    dataset.borderColor || dataset.backgroundColor,
                  opacity: visibility[dataset.label] ? 1 : 0.3,
                }}
              >
                <div
                  style={{
                    backgroundColor:
                      dataset.borderColor || dataset.backgroundColor,
                    opacity: visibility[dataset.label] ? 1 : 0.3,
                  }}
                  className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                ></div>
              </div>
              <span style={{ opacity: visibility[dataset.label] ? 1 : 0.3 }}>
                {dataset.label}
              </span>
            </div>
          ))}
        </div>
        <div
          className={`flex items-center gap-2 ${
            isFullscreen
              ? "w-full justify-end sm:w-auto sm:justify-start"
              : "ml-auto"
          }`}
        >
          {isSuperUser && (
            <>
              <span
                className="text-xs font-semibold"
                style={{ color: COLORS.darkerGray }}
              >
                Graph Type:
              </span>
              <Select
                value={graphType}
                onChange={setGraphType}
                options={graphTypeOptions}
                className="w-[210px]"
              />
            </>
          )}
          <RangePicker
            defaultValue={selectedDates}
            value={selectedDates}
            onChange={handleDateChange}
            format={UI_DATE_FORMAT}
            className="date flex items-center justify-center"
          />
        </div>
      </div>
      {isSuperUser && (
        <Alert
          type="info"
          showIcon={false}
          className={`${isFullscreen ? "mb-2" : "mb-3"} !py-1`}
          style={{ border: "none", backgroundColor: "transparent" }}
          message={
            <div className="text-xs leading-5" style={{ color: COLORS.primaryBlue }}>
              <span className="font-semibold">{activeGraphInfo.short}</span>{" "}
              <span>{activeGraphInfo.relevance}</span>
              <Popover
                trigger="click"
                placement="bottomRight"
                title="How this graph is calculated"
                content={
                  <div className="max-w-[380px] text-xs leading-5">
                    <div className="mb-1 font-semibold">{activeGraphInfo.relevance}</div>
                    {activeGraphInfo.details.map((detail) => (
                      <div key={detail}>{detail}</div>
                    ))}
                  </div>
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="Graph calculation details"
                  icon={<InfoCircleOutlined style={{ color: COLORS.primaryBlue }} />}
                  className="!ml-1 !h-auto !w-auto !min-w-0 !p-0 align-middle"
                />
              </Popover>
            </div>
          }
        />
      )}
      <div
        className={`w-full ${
          isFullscreen
            ? "min-h-0 flex-1 overflow-x-auto overflow-y-hidden"
            : "overflow-x-auto"
        }`}
      >
        <div className={isFullscreen ? "h-full min-w-[760px]" : "min-w-[640px]"}>
          {graphType === "heatmap" ? (
            <Scatter data={heatmapChartData} options={heatmapOptions} />
          ) : graphType === "quantile" ? (
            <Line data={quantileChartData} options={quantileOptions} />
          ) : graphType === "specMix" ? (
            <Line data={specMixChartData} options={specMixOptions} />
          ) : graphType === "ecdf" ? (
            <Line data={ecdfChartData} options={ecdfOptions} />
          ) : graphType === "delta" ? (
            <Line data={deltaChartData} options={deltaOptions} />
          ) : graphType === "kpi" ? (
            <Line data={kpiChartData} options={kpiOptions} />
          ) : (
            <Line
              data={chartData}
              options={chartOptions}
              plugins={[greenBandPlugin]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export { DistributionGraph };
